/* eslint-env browser */
import fetch from 'isomorphic-fetch';

import { blob, errorize, jsonize } from './converters';
import { authHeader, hardLogout, jwtAuthHeader, refreshToken } from './auth';
import config from 'config';

export const ABSOLUTE_URL_REGEX = new RegExp('^(?:[a-z]+:)?//', 'i');

export function makeAbsoluteURL(url) {
  // This is faster than window.URL
  return ABSOLUTE_URL_REGEX.test(url) ? url : `${config.BASE_URL}${url}`;
}

function request(method, baseHeaders, url, body, headers = {}, getBlob = false) {
  const defaultHeaders = { 'Accept-Language': 'en-US' };

  let options = {
    method,
    headers: Object.assign(
      {},
      baseHeaders,
      body ? { 'Content-Type': 'application/json', ...defaultHeaders } : defaultHeaders,
      headers
    ),
  };

  if (body)
    options.body =
      headers && headers['Content-Type'] && headers['Content-Type'] === 'multipart/form-data'
        ? body
        : JSON.stringify(body);

  return fetch(makeAbsoluteURL(url), options)
    .then(!getBlob ? jsonize : blob)
    .catch(errorize);
}

function authorizedRequestWithUser(method, url, body, headers, getBlob) {
  return refreshToken().then((maybeNewUser) =>
    request(method, authHeader(maybeNewUser), url, body, headers, getBlob).catch((error) => {
      if (error.code === 401) {
        hardLogout();
      } else {
        throw error;
      }
    })
  );
}

function authorizedRequestWithToken(method, url, body, headers, getBlob) {
  return refreshToken().then(() =>
    request(method, jwtAuthHeader(), url, body, headers, getBlob).catch((error) => {
      if (error.code === 401) {
        hardLogout();
      } else {
        throw error;
      }
    })
  );
}

function authorizedRequest(method, url, body, headers, getBlob) {
  const useJwtAuth = localStorage.getItem('useSAML');
  const authorizedRequestFunction = useJwtAuth
    ? authorizedRequestWithToken
    : authorizedRequestWithUser;
  return authorizedRequestFunction(method, url, body, headers, getBlob);
}

export const get = (...args) => authorizedRequest('GET', ...args);
export const post = (...args) => authorizedRequest('POST', ...args);
export const put = (...args) => authorizedRequest('PUT', ...args);
export const patch = (...args) => authorizedRequest('PATCH', ...args);
export const _delete = (...args) => authorizedRequest('DELETE', ...args);
export const getBlob = (url, body, headers) => authorizedRequest('GET', url, null, headers, true);

export const rawGet = (...args) => request('GET', {}, ...args);
export const rawPost = (...args) => request('POST', {}, ...args);
export const rawPut = (...args) => request('PUT', {}, ...args);
export const rawPatch = (...args) => request('PATCH', {}, ...args);
export const rawDelete = (...args) => request('DELETE', {}, ...args);

function mergeMoreResults({ next, results = [] }) {
  return !next
    ? Promise.resolve(results)
    : get(next)
        .then(mergeMoreResults)
        .then((moreResults) => results.concat(moreResults));
}

export const getAll = (url, page_size = config.PAGE_SIZE_FOR_ALL) => {
  const sep = url.indexOf('?') >= 0 ? '&' : '?';
  return get(`${url}${sep}page_size=${page_size}`).then(mergeMoreResults);
};

export const getError = async (err) => {
  if (!err || !err.response || !err.response.json) {
    return err;
  }
  try {
    const error = await err.response.json();
    console.error('[HTTP] error', error);
    return error;
  } catch (error) {
    console.error('[HTTP] invalid json', error);
    return error;
  }
};
